import React from "react";
import { Card, Progress, Statistic, Table, Tag, Space, Button, message, Spin } from "antd";
import { useQuery } from "@tanstack/react-query";
import { getUserProgress, getUserCourseProgress } from "./api";
import { BookOutlined, CheckCircleOutlined, ClockCircleOutlined, PlayCircleOutlined, ArrowLeftOutlined } from "@ant-design/icons";

interface UserProgressProps {
  userId: number;
  onBack?: () => void;
}

interface CourseProgress {
  courseId: number;
  courseTitle: string;
  courseDescription?: string;
  totalLessons: number;
  completedLessons: number;
  inProgressLessons: number;
  notStartedLessons: number;
  progressPercentage: number;
  enrolledAt: string;
}

interface LessonProgress {
  lessonId: number;
  lessonTitle: string;
  sortOrder: number;
  progressStatus: 'not_started' | 'in_progress' | 'completed' | 'failed';
  testAttempts: number;
  openQuestionAttempts: number;
  examType?: 'test' | 'open' | null;
  startedAt?: string | null;
  completedAt?: string | null;
}

const UserProgress: React.FC<UserProgressProps> = ({ userId, onBack }) => {
  const { data: userProgress, isLoading, error } = useQuery({
    queryKey: ['userProgress', userId],
    queryFn: () => getUserProgress(userId),
  });

  const [selectedCourse, setSelectedCourse] = React.useState<number | null>(null);

  const { data: courseProgress, isLoading: courseLoading } = useQuery({
    queryKey: ['courseProgress', userId, selectedCourse],
    queryFn: () => selectedCourse ? getUserCourseProgress(userId, selectedCourse) : null,
    enabled: !!selectedCourse,
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
      case 'passed':
        return 'green';
      case 'in_progress':
        return 'blue';
      case 'failed':
      case 'timeout':
        return 'red';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Завершен';
      case 'in_progress':
        return 'В процессе';
      case 'not_started':
        return 'Не начат';
      case 'failed':
        return 'Провален';
      case 'passed':
        return 'Пройден';
      case 'timeout':
        return 'Время истекло';
      default:
        return status;
    }
  };

  const getExamTypeText = (examType: string) => {
    switch (examType) {
      case 'test':
        return 'Тест';
      case 'open':
        return 'Открытый вопрос';
      default:
        return '';
    }
  };

  const courseColumns = [
    {
      title: "Курс",
      dataIndex: "courseTitle",
      key: "courseTitle",
      render: (title: string, record: CourseProgress) => (
        <div>
          <div style={{ fontWeight: '500' }}>{title}</div>
          {record.courseDescription && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              {record.courseDescription}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Прогресс",
      key: "progress",
      width: 200,
      render: (record: CourseProgress) => (
        <div>
          <Progress 
            percent={record.progressPercentage} 
            size="small"
            status={record.progressPercentage === 100 ? 'success' : 'active'}
          />
          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
            {record.completedLessons} из {record.totalLessons} уроков
          </div>
        </div>
      ),
    },
    {
      title: "Статистика",
      key: "stats",
      width: 200,
      render: (record: CourseProgress) => (
        <div style={{ fontSize: '12px' }}>
          <div>✅ Завершено: {record.completedLessons}</div>
          <div>🔄 В процессе: {record.inProgressLessons}</div>
          <div>⏳ Не начато: {record.notStartedLessons}</div>
        </div>
      ),
    },
    {
      title: "Записался",
      dataIndex: "enrolledAt",
      key: "enrolledAt",
      width: 120,
      render: (date: string) => new Date(date).toLocaleDateString('ru-RU'),
    },
    {
      title: "Действия",
      key: "actions",
      width: 120,
      render: (record: CourseProgress) => (
        <Button
          type="primary"
          size="small"
          onClick={() => setSelectedCourse(record.courseId)}
        >
          Детали
        </Button>
      ),
    },
  ];

  const lessonColumns = [
    {
      title: "№",
      dataIndex: "sortOrder",
      key: "sortOrder",
      width: 60,
    },
    {
      title: "Урок",
      dataIndex: "lessonTitle",
      key: "lessonTitle",
      render: (title: string, record: LessonProgress) => (
        <div>
          <div style={{ fontWeight: '500' }}>{title}</div>
          {record.examType && (
            <Tag color="blue" style={{ marginTop: '4px' }}>
              {getExamTypeText(record.examType)}
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: "Прогресс",
      key: "progress",
      width: 120,
      render: (record: LessonProgress) => (
        <Tag color={getStatusColor(record.progressStatus)}>
          {getStatusText(record.progressStatus)}
        </Tag>
      ),
    },
    {
      title: "Попытки тестов",
      key: "testAttempts",
      width: 120,
      render: (record: LessonProgress) => {
        if (record.examType !== 'test') return '-';
        return (
          <div style={{ textAlign: 'center' }}>
            <span style={{ fontSize: '16px', fontWeight: 'bold' }}>
              {record.testAttempts}
            </span>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.testAttempts === 0 ? 'Нет попыток' : 
               record.testAttempts === 1 ? 'попытка' :
               record.testAttempts < 5 ? 'попытки' : 'попыток'}
            </div>
          </div>
        );
      },
    },
    {
      title: "Попытки вопросов",
      key: "openQuestionAttempts",
      width: 120,
      render: (record: LessonProgress) => {
        if (record.examType !== 'open') return '-';
        return (
          <div style={{ textAlign: 'center' }}>
            <span style={{ fontSize: '16px', fontWeight: 'bold' }}>
              {record.openQuestionAttempts}
            </span>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.openQuestionAttempts === 0 ? 'Нет попыток' : 
               record.openQuestionAttempts === 1 ? 'попытка' :
               record.openQuestionAttempts < 5 ? 'попытки' : 'попыток'}
            </div>
          </div>
        );
      },
    },
    {
      title: "Даты",
      key: "dates",
      width: 180,
      render: (record: LessonProgress) => (
        <div style={{ fontSize: '12px' }}>
          {record.startedAt && (
            <div>Начал: {new Date(record.startedAt).toLocaleString('ru-RU', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit'
            })}</div>
          )}
          {record.completedAt && (
            <div>Завершил: {new Date(record.completedAt).toLocaleString('ru-RU', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit'
            })}</div>
          )}
          {(record.testAttempts > 0 || record.openQuestionAttempts > 0) && (
            <div style={{ marginTop: '4px', color: '#666' }}>
              {record.examType === 'test' && record.testAttempts > 0 && (
                <div>Тестов: {record.testAttempts}</div>
              )}
              {record.examType === 'open' && record.openQuestionAttempts > 0 && (
                <div>Вопросов: {record.openQuestionAttempts}</div>
              )}
            </div>
          )}
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', color: 'red' }}>
        Ошибка загрузки прогресса: {(error as Error).message}
      </div>
    );
  }

  if (!userProgress) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        Данные не найдены
      </div>
    );
  }

  return (
    <div>
      {/* Информация о пользователе */}
      <Card 
        style={{ marginBottom: 24 }}
        extra={onBack && (
          <Button onClick={onBack} icon={<ArrowLeftOutlined />}>
            Назад к списку пользователей
          </Button>
        )}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
          <div>
            <h3 style={{ margin: 0 }}>
              {userProgress.user.firstName} {userProgress.user.lastName}
            </h3>
            <div style={{ color: '#666' }}>{userProgress.user.email}</div>
            <Tag color="blue">{userProgress.user.role.name}</Tag>
          </div>
        </div>

        {/* Общая статистика */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '16px' 
        }}>
          <Statistic
            title="Записался на курсов"
            value={userProgress.overallStats.totalEnrolledCourses}
            prefix={<BookOutlined />}
            valueStyle={{ color: '#1976d2' }}
          />
          <Statistic
            title="Всего уроков"
            value={userProgress.overallStats.totalLessons}
            prefix={<PlayCircleOutlined />}
            valueStyle={{ color: '#1976d2' }}
          />
          <Statistic
            title="Завершил уроков"
            value={userProgress.overallStats.totalCompletedLessons}
            prefix={<CheckCircleOutlined />}
            valueStyle={{ color: '#52c41a' }}
          />
          <Statistic
            title="Общий прогресс"
            value={userProgress.overallStats.overallProgressPercentage}
            suffix="%"
            prefix={<ClockCircleOutlined />}
            valueStyle={{ color: '#1890ff' }}
          />
        </div>
      </Card>

      {/* Список курсов */}
      {!selectedCourse && (
        <Card title="Курсы пользователя">
          <Table
            dataSource={userProgress.coursesProgress}
            columns={courseColumns}
            rowKey="courseId"
            pagination={false}
          />
        </Card>
      )}

      {/* Детали курса */}
      {selectedCourse && courseProgress && (
        <Card 
          title={`Детали курса: ${courseProgress.course.title}`}
          extra={
            <Button onClick={() => setSelectedCourse(null)}>
              Назад к списку курсов
            </Button>
          }
        >
          {/* Прогресс курса */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
              <Progress 
                type="circle" 
                percent={courseProgress.progress.progressPercentage}
                size={80}
                status={courseProgress.progress.progressPercentage === 100 ? 'success' : 'active'}
              />
              <div>
                <h4 style={{ margin: 0 }}>Прогресс курса</h4>
                <div style={{ color: '#666' }}>
                  {courseProgress.progress.completedLessons} из {courseProgress.progress.totalLessons} уроков завершено
                </div>
              </div>
            </div>

            {/* Статистика курса */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
              gap: '16px' 
            }}>
              <Statistic
                title="Завершено"
                value={courseProgress.progress.completedLessons}
                valueStyle={{ color: '#52c41a' }}
              />
              <Statistic
                title="В процессе"
                value={courseProgress.progress.inProgressLessons}
                valueStyle={{ color: '#1890ff' }}
              />
              <Statistic
                title="Не начато"
                value={courseProgress.progress.notStartedLessons}
                valueStyle={{ color: '#666' }}
              />
            </div>
          </div>

          {/* Список уроков */}
          <Table
            dataSource={courseProgress.lessons}
            columns={lessonColumns}
            rowKey="lessonId"
            pagination={false}
            loading={courseLoading}
          />
        </Card>
      )}
    </div>
  );
};

export default UserProgress; 